<script>
	import Prism from 'prismjs';
	import { onMount } from 'svelte';

	let CodeJar;
	export let value = '';
	export let origin = null;
	export let nestedKeys = null;
	export let description = null;
	export let title = null;
	export let syntax = 'javascript';
	const highlight = (code, syntax) => Prism.highlight(code, Prism.languages[syntax], syntax);

	const codeEditorEdit = (e) => {
		function leaf(obj, path, value) {
			const pList = path.split('.');
			const key = pList.pop();
			const pointer = pList.reduce((accumulator, currentValue) => {
				if (accumulator[currentValue] === undefined) accumulator[currentValue] = {};
				return accumulator[currentValue];
			}, obj);
			pointer[key] = value;
			return obj;
		}
		let changedValue = e.detail.value;
		let changedParsed;
		try {
			changedParsed = JSON.parse(changedValue);
		} catch (error) {
			changedParsed = changedValue;
		}
		if (nestedKeys !== null) {
			origin = leaf(origin, nestedKeys, changedParsed);
		} else {
			origin = changedParsed;
		}
	};

	onMount(async () => {
		({ CodeJar } = await import('@novacbn/svelte-codejar'));
	});
</script>

<div class="editor-wrapper">
	{#if title}
		<h2>{title}</h2>
	{/if}
	{#if description}
		<p>{description}</p>
	{/if}
	{#if CodeJar}
		<CodeJar on:change={codeEditorEdit} bind:value {highlight} {syntax} />
	{:else}
		<pre><code>{`${value}`}</code></pre>
	{/if}
</div>

<style src="./codeEditor.css" global>/**
 * Dracula Theme originally by Zeno Rocha [@zenorocha]
 * https://draculatheme.com/
 *
 * Ported for PrismJS by Albert Vallverdu [@byverdu]
 */
:global(.editor-wrapper) :global(h2) {
	font-family: sans-serif;
	line-height: 1;
	margin-bottom: 10px;
}
:global(.editor-wrapper) :global(p) {
	font-family: sans-serif;
	margin-top: 0px;
}

:global(code[class*='language-']),
:global(pre[class*='language-']) {
	color: #f8f8f2;
	background: none;
	text-shadow: 0 1px rgba(0, 0, 0, 0.3);
	font-family: Consolas, Monaco, 'Andale Mono', 'Ubuntu Mono', monospace;
	text-align: left;
	white-space: pre;
	word-spacing: normal;
	word-break: normal;
	word-wrap: normal;
	line-height: 1.5;
	-moz-tab-size: 4;
	-o-tab-size: 4;
	tab-size: 4;
	-webkit-hyphens: none;
	-moz-hyphens: none;
	-ms-hyphens: none;
	hyphens: none;
	border-radius: 4px;
}

/* Code blocks */
:global(pre[class*='language-']) {
	padding: 1em;
	margin: 0.5em 0;
	overflow: auto;
	border-radius: 0.3em;
}

:global(:not(pre)) > :global(code[class*='language-']),
:global(pre[class*='language-']) {
	background: #282a36;
}

/* Inline code */
:global(:not(pre)) > :global(code[class*='language-']) {
	padding: 0.1em;
	border-radius: 0.3em;
	white-space: normal;
}

:global(.token.comment),
:global(.token.prolog),
:global(.token.doctype),
:global(.token.cdata) {
	color: #6272a4;
}

:global(.token.punctuation) {
	color: #f8f8f2;
}

:global(.namespace) {
	opacity: 0.7;
}

:global(.token.property),
:global(.token.tag),
:global(.token.constant),
:global(.token.symbol),
:global(.token.deleted) {
	color: #ff79c6;
}

:global(.token.boolean),
:global(.token.number) {
	color: #bd93f9;
}

:global(.token.selector),
:global(.token.attr-name),
:global(.token.string),
:global(.token.char),
:global(.token.builtin),
:global(.token.inserted) {
	color: #50fa7b;
}

:global(.token.operator),
:global(.token.entity),
:global(.token.url),
:global(.language-css) :global(.token.string),
:global(.style) :global(.token.string),
:global(.token.variable) {
	color: #f8f8f2;
}

:global(.token.atrule),
:global(.token.attr-value),
:global(.token.function),
:global(.token.class-name) {
	color: #f1fa8c;
}

:global(.token.keyword) {
	color: #8be9fd;
}

:global(.token.regex),
:global(.token.important) {
	color: #ffb86c;
}

:global(.token.important),
:global(.token.bold) {
	font-weight: bold;
}

:global(.token.italic) {
	font-style: italic;
}

:global(.token.entity) {
	cursor: help;
}
</style>
